<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCarBookingStore } from 'dashboard/stores/carBooking';
import NextButton from 'dashboard/components-next/button/Button.vue';
import OfferCard from '../OfferCard.vue';

const props = defineProps({
  conversationId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const carBooking = useCarBookingStore();

const session = computed(() => carBooking.sessionFor(props.conversationId));

const sharedOffers = computed(() =>
  (session.value.offers || []).filter(offer =>
    (session.value.shared || []).includes(offer.token)
  )
);

const selected = computed(() => session.value.selected);

const pick = token => carBooking.setSelected(props.conversationId, token);

const back = () => carBooking.setStep(props.conversationId, 'offers');

const continueToBook = () => carBooking.setStep(props.conversationId, 'book');
</script>

<template>
  <div class="flex h-full flex-col">
    <div class="flex flex-1 flex-col gap-3 overflow-y-auto">
      <p class="m-0 text-sm text-n-slate-11">
        {{ t('CONVERSATION.CAR_BOOKING.CHOICE.HINT') }}
      </p>

      <ul class="flex flex-col gap-3">
        <li
          v-for="(offer, index) in sharedOffers"
          :key="offer.token"
          class="cursor-pointer overflow-hidden rounded-xl border bg-n-surface-1 transition-colors"
          :class="
            selected === offer.token
              ? 'border-n-brand shadow-[0_0_0_1px] shadow-n-brand'
              : 'border-n-weak hover:border-n-strong'
          "
          @click="pick(offer.token)"
        >
          <div class="flex items-start gap-3.5 p-3.5">
            <span
              class="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-md border-2 transition-colors"
              :class="
                selected === offer.token
                  ? 'border-n-brand bg-n-brand'
                  : 'border-n-strong'
              "
            >
              <span
                v-if="selected === offer.token"
                class="i-lucide-check size-3.5 text-white"
              />
            </span>
            <div class="min-w-0 flex-1">
              <OfferCard :offer="offer" :index="index" />
            </div>
          </div>

          <div
            class="flex items-center justify-end border-t border-n-weak px-3.5 py-2.5"
          >
            <NextButton
              link
              slate
              sm
              :label="t('CONVERSATION.CAR_BOOKING.OFFERS.DETAILS')"
              @click.stop="carBooking.openDetail(conversationId, offer.token)"
            />
          </div>
        </li>
      </ul>
    </div>

    <div
      class="mt-4 flex items-center gap-3 border-t border-n-weak bg-n-surface-2 px-4 py-3"
    >
      <NextButton
        faded
        slate
        :label="t('CONVERSATION.CAR_BOOKING.CHOICE.BACK')"
        @click="back"
      />
      <NextButton
        solid
        blue
        class="ml-auto"
        :label="t('CONVERSATION.CAR_BOOKING.CHOICE.CONTINUE')"
        :disabled="!selected"
        @click="continueToBook"
      />
    </div>
  </div>
</template>
